/**
 * Which server this console talks to: a self-hosted Orgus hub, or Orgus Cloud (the Cloudflare
 * control plane, whose GET /api/health says controlPlane and lists the modules it runs). An absent
 * module reads differently on each: a hub can be updated by its owner, Orgus Cloud cannot be
 * updated by anyone using it, so it says the feature is coming and what works instead (plan 1B).
 * Demo data (?mock=1) plays Orgus Cloud with ?server=cloud.
 */
import { useEffect, useState } from 'react';
import { ApiError, MODULE_ABSENT_CODE } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { isMockRequested } from '../../lib/mock.ts';

export type ServerKind = 'hub' | 'cloud' | 'unknown';

export interface ServerInfo {
  kind: ServerKind;
  /** The modules Orgus Cloud runs; null on a hub or before it answered. */
  modules: readonly string[] | null;
}

const UNKNOWN: ServerInfo = { kind: 'unknown', modules: null };

/** What a GET /api/health body says about the server. */
export function serverInfoOf(health: unknown): ServerInfo {
  if (!health || typeof health !== 'object') return UNKNOWN;
  const h = health as { controlPlane?: unknown; modules?: unknown };
  if (typeof h.controlPlane !== 'string') return { kind: 'hub', modules: null };
  const modules = Array.isArray(h.modules) ? h.modules.filter((m): m is string => typeof m === 'string') : null;
  return { kind: 'cloud', modules };
}

/** What Orgus Cloud runs today (apps/control-plane/src/ext/index.ts MODULE_NAMES): demo data plays it. */
export const CLOUD_MODULES: readonly string[] = ['trust', 'ai', 'session-control', 'diff', 'auth', 'pairing', 'policy', 'launch-accounts', 'machine-controls', 'approvals', 'costs', 'missions', 'notify', 'fleet', 'readiness', 'retention', 'audit-ops', 'onboarding', 'templates', 'lifecycle', 'views', 'flows', 'finance', 'project-protocol', 'term', 'landing', 'watch', 'scheduler', 'teams', 'asks', 'secrets', 'agent-usage', 'comments', 'attachments', 'control', 'discipline', 'assistant', 'aijobs'];

/**
 * The hub modules the console calls and the /api paths only they answer (apps/hub/src/ext). A
 * path ending in '$' matches exactly; the rest match as a prefix. Paths Orgus Cloud's gateway
 * serves itself (/api/landing/:project/:branch, /api/audit) are left out or matched exactly.
 */
const MODULE_PATHS: Readonly<Record<string, readonly string[]>> = {
  'acp-registry': ['/api/acp-registry'],
  'agent-environment': ['/api/agent-environment'],
  'agent-install': ['/api/agent-install'],
  'agent-usage': ['/api/agent-usage'],
  aijobs: ['/api/aijobs'],
  asks: ['/api/asks'],
  assistant: ['/api/assistant'],
  attachments: ['/api/attachments'],
  'audit-ops': ['/api/ops', '/api/security', '/api/audit/query$'],
  ci: ['/api/ci'],
  comments: ['/api/comments'],
  control: ['/api/control'],
  discipline: ['/api/standards'],
  finance: ['/api/finance'],
  flows: ['/api/flows'],
  forecasts: ['/api/forecasts'],
  landing: ['/api/landing$'],
  licensing: ['/api/licensing'],
  lifecycle: ['/api/hub/lifecycle'],
  live: ['/api/live'],
  onboarding: ['/api/onboarding'],
  'project-protocol': ['/api/project-protocol'],
  scheduler: ['/api/scheduler'],
  secrets: ['/api/secrets'],
  stack: ['/api/stack'],
  teams: ['/api/teams', '/api/presence'],
  templates: ['/api/templates'],
  term: ['/api/term', '/api/browser-sessions'],
  views: ['/api/ext/views'],
  watch: ['/api/watch'],
};

/** How an absent module reads in a sentence ("Project checks are coming to the web app"). */
const MODULE_FEATURE: Readonly<Record<string, { feature: string; plural?: boolean }>> = {
  'audit-ops': { feature: 'Security' },
  assistant: { feature: 'The assistant' },
  finance: { feature: 'Spend reports', plural: true },
  flows: { feature: 'Flows', plural: true },
  landing: { feature: 'Landing queues', plural: true },
  'project-protocol': { feature: 'Project checks', plural: true },
  secrets: { feature: 'Secrets', plural: true },
  teams: { feature: 'Teams', plural: true },
  term: { feature: 'Terminals', plural: true },
  control: { feature: 'Session control' },
  scheduler: { feature: 'Mission scheduling' },
  watch: { feature: 'Watch' },
};

/** Routes of modules Orgus Cloud runs, but not every route of them yet. */
const PATH_FEATURE: readonly (readonly [string, { feature: string; plural?: boolean }])[] = [
  // None today: email runs on Orgus Cloud through Cloudflare Email Sending.
];

/** The module whose routes this path belongs to, if the console knows it. */
export function moduleForPath(path: string): string | null {
  const p = path.split('?')[0]!;
  for (const [m, prefixes] of Object.entries(MODULE_PATHS)) {
    for (const x of prefixes) {
      if (x.endsWith('$') ? p === x.slice(0, -1) : p === x || p.startsWith(`${x}/`)) return m;
    }
  }
  return null;
}

/**
 * Routes of modules Orgus Cloud runs that it does not answer yet (tracked by the cloud-gaps gate,
 * e2e/contracts/cloud-gaps.test.ts). Remove a line when the control plane ports the route.
 */
export const CLOUD_MISSING_ROUTES: readonly string[] = [];

/**
 * Whether this server says it does not run the module (a module name) or the route (a path
 * starting with /api). Only Orgus Cloud says so; a hub, or a server not yet asked, never.
 */
export function moduleAbsent(info: ServerInfo, module: string): boolean {
  if (info.kind !== 'cloud' || info.modules === null) return false;
  if (module.startsWith('/api/')) {
    const p = module.split('?')[0]!;
    if (CLOUD_MISSING_ROUTES.some((x) => p === x || p.startsWith(`${x}/`))) return true;
    const m = moduleForPath(p);
    return m !== null && !info.modules.includes(m);
  }
  return !info.modules.includes(module);
}

const CLOUD_INSTEAD = 'Sessions, missions, approvals, costs and machines all work here.';

/** What a person can do now, staying in the hosted web app (R-FN-2: never send them to a local hub). */
export const CLOUD_NEXT = 'It is on its way here.';

/** The title for a feature the web app does not offer yet, in words anyone can act on. */
export function notHereYet(feature: string, plural = false): string {
  return `${feature} ${plural ? 'are' : 'is'} coming to the web app`;
}

/** Rewrites a hosted-server refusal ("not-on-control-plane") into plain words, keeping what failed. */
export function plainHostedRefusal(e: UiError): UiError {
  return { ...e, why: 'The web app cannot do this yet. It is on its way.', next: 'Carry on with sessions, missions and approvals here, or ask your admin to turn it on.' };
}

/** The calm error for a feature Orgus Cloud does not run yet (ErrorState shows it as an empty state). */
export function absentError(module: string | null, path = ''): ApiError {
  const p = path.split('?')[0]!;
  const f = (module && MODULE_FEATURE[module]) || PATH_FEATURE.find(([x]) => p === x || p.startsWith(`${x}/`))?.[1] || { feature: 'This feature' };
  const ui: UiError = { what: notHereYet(f.feature, f.plural), why: CLOUD_INSTEAD, next: CLOUD_NEXT, code: MODULE_ABSENT_CODE, status: 404 };
  return new ApiError(ui, 404);
}

let primed = false;

/** Ask the server what it runs as the console starts, so the API guard knows before any call. */
export function primeServer(): void {
  primed = true;
  void load();
}

/** The server info for the API guard: once primed (main.tsx), every call waits for it. */
export function serverInfo(): Promise<ServerInfo> {
  return primed ? load() : Promise.resolve(UNKNOWN);
}

let cached: Promise<ServerInfo> | null = null;

function mockServer(): ServerInfo {
  try {
    return new URLSearchParams(window.location.search).get('server') === 'cloud' ? { kind: 'cloud', modules: CLOUD_MODULES } : { kind: 'hub', modules: null };
  } catch {
    return { kind: 'hub', modules: null };
  }
}

function load(): Promise<ServerInfo> {
  if (isMockRequested()) return Promise.resolve(mockServer());
  cached ??= fetch('/api/health', { cache: 'no-store' })
    .then((r) => r.json() as Promise<unknown>)
    .then(serverInfoOf, () => {
      cached = null;
      return UNKNOWN;
    });
  return cached;
}

/** The server kind, asked once per page load. */
export function useServer(): ServerInfo {
  const [info, setInfo] = useState<ServerInfo>(UNKNOWN);
  useEffect(() => {
    let live = true;
    void load().then((i) => live && setInfo(i));
    return () => {
      live = false;
    };
  }, []);
  return info;
}

/** True once the server said it does not run this module (Orgus Cloud only; never on a hub). */
export function useModuleAbsent(module: string): boolean {
  return moduleAbsent(useServer(), module);
}

export interface AbsentCopy {
  title: string;
  description: string;
}

export interface AbsentModule {
  /** The feature, as a sentence subject: "The assistant", "Mission scheduling". */
  feature: string;
  /** The feature is a plural noun ("Agent accounts are coming"). */
  plural?: boolean;
  /** What a self-hosted hub says: its title and the update advice. */
  hub: AbsentCopy;
  /** On Orgus Cloud: what works instead, as one or two sentences. */
  instead: string;
}

/** The empty state for a module this server does not run. */
export function absentModuleCopy(kind: ServerKind, m: AbsentModule): AbsentCopy {
  if (kind === 'cloud') return { title: notHereYet(m.feature, m.plural), description: `${CLOUD_NEXT} ${m.instead}` };
  return m.hub;
}
